import { COUNTRIES_WITH_MONTHLY_DURATION, WEEKS_PER_MONTH } from "constants/configConstants";

export type PriceHistogramBucket = {
  lo: number;
  hi: number;
  count: number;
};

export type PriceHistogram = {
  min: number;
  max: number;
  interval: number;
  currency?: string;
  buckets: PriceHistogramBucket[];
  catch_all?: { lo: number; count: number; label: string };
};

/** The `price_stats` fields of the inventory_filter_count aggregation payload. */
export type PriceFloorStats = {
  min_weekly_price?: number | string | null;
  min_monthly_price?: number | string | null;
  max_weekly_price?: number | string | null;
  max_monthly_price?: number | string | null;
};

export const isMonthlyRegion = (region: any) =>
  COUNTRIES_WITH_MONTHLY_DURATION.includes(region?.location?.country?.short_name);

/**
 * Cheapest live price, used as the slider's floor.
 * `price_histogram.min` is the bucket grid's origin (0 for London), not a real room.
 */
export const getMinAvailablePrice = (stats: PriceFloorStats | null | undefined, region: any) => {
  if (!stats) return null;
  const weekly = Number(stats.min_weekly_price);
  const monthly = Number(stats.min_monthly_price);
  // Monthly regions quote per month, so a weekly figure counts only once converted.
  const candidates = isMonthlyRegion(region)
    ? [monthly, Number.isFinite(weekly) ? weekly * WEEKS_PER_MONTH : NaN]
    : [weekly];
  const valid = candidates.filter((value) => Number.isFinite(value) && value > 0);
  return valid.length ? Math.floor(Math.min(...valid)) : null;
};

/**
 * Dearest live price. `price_histogram.max` is only where the catch-all ("660+") starts.
 * An open-ended budget is closed at this price, because the API rejects a one-sided range.
 */
export const getMaxAvailablePrice = (stats: PriceFloorStats | null | undefined, region: any) => {
  if (!stats) return null;
  const weekly = Number(stats.max_weekly_price);
  const monthly = Number(stats.max_monthly_price);
  const candidates = isMonthlyRegion(region)
    ? [monthly, Number.isFinite(weekly) ? weekly * WEEKS_PER_MONTH : NaN]
    : [weekly];
  const valid = candidates.filter((value) => Number.isFinite(value) && value > 0);
  return valid.length ? Math.ceil(Math.max(...valid)) : null;
};

/** Region-level prices, used only when the count response carries no `price_stats` yet. */
const regionFallbackBounds = (region: any) => {
  const range = region?.region_inventory_price_range;
  const monthly = isMonthlyRegion(region);
  const min = Number(monthly ? range?.min_available_price : range?.min_weekly_price);
  const max = Number(monthly ? range?.max_available_price : range?.max_weekly_price);
  return {
    floor: Number.isFinite(min) && min > 0 ? Math.floor(min) : null,
    ceiling: Number.isFinite(max) && max > 0 ? Math.ceil(max) : null,
  };
};

/** The live price span the Budget panel draws its slider on. One source for every producer. */
export const getBudgetBounds = (priceStats: PriceFloorStats | null | undefined, region: any) => {
  const fallback = regionFallbackBounds(region);
  return {
    floor: getMinAvailablePrice(priceStats, region) ?? fallback.floor,
    ceiling: getMaxAvailablePrice(priceStats, region) ?? fallback.ceiling,
  };
};

const asPrice = (raw: unknown) => {
  const value = Number(String(raw ?? "").trim());
  return Number.isFinite(value) && value > 0 ? value : null;
};

/**
 * Normalises a budget from any producer — the histogram panel, the Smart (NLP) filter, or the URL
 * — onto the same two numbers.
 *
 * `range_available_price` has no way to say "no maximum": an open end travels as the region's
 * dearest price, so the number itself is the encoding. Producers that fill their own ends from a
 * different source write a different "no maximum", and the panel then silently rewrites the budget
 * (and the result set) the moment the user touches it. Missing ends here come from the same
 * `price_stats` bounds the slider is drawn on, and stated ends are clamped into them, so no
 * producer can write a budget the panel cannot represent.
 */
export const resolveBudgetRange = (
  range: string | string[] | undefined | null,
  priceStats: PriceFloorStats | null | undefined,
  region: any,
): [string, string] | null => {
  const [rawMin, rawMax] = Array.isArray(range)
    ? range.map((part) => String(part ?? ""))
    : String(range ?? "").split(",");
  const { floor, ceiling } = getBudgetBounds(priceStats, region);

  const lo = asPrice(rawMin) ?? floor;
  const hi = asPrice(rawMax) ?? ceiling;
  if (lo === null || hi === null) return null;

  const clamp = (price: number) =>
    Math.min(Math.max(price, floor ?? price), Math.max(ceiling ?? price, floor ?? price));
  const clampedLo = clamp(lo);
  // An end above the ceiling is an open top, which is what the ceiling already means.
  const clampedHi = Math.max(clamp(hi), clampedLo);
  return [String(Math.trunc(clampedLo)), String(Math.trunc(clampedHi))];
};

/** A histogram's buckets and its catch-all as one list, so re-binning can treat them alike. */
const toSlices = (histogram: PriceHistogram, top: number): PriceHistogramBucket[] => {
  const slices = histogram.buckets.map((bucket) => ({
    lo: bucket.lo,
    hi: bucket.hi,
    count: bucket.count || 0,
  }));
  if (histogram.catch_all) {
    slices.push({
      lo: histogram.catch_all.lo,
      hi: Math.max(top, histogram.catch_all.lo + histogram.interval),
      count: histogram.catch_all.count || 0,
    });
  }
  return slices;
};

/**
 * The filtered counts spread onto a fixed bucket grid, by how much of each filtered bucket overlaps
 * each grid bucket.
 *
 * The API re-computes the histogram per filter set, so its buckets move: add a move-in month and
 * London's bars go from 0-660 in 20s to 200-1000 in 25s. Drawn directly, the panel changes how many
 * bars it has and where the "+" sits every time another filter is touched. Keeping the grid and
 * re-binning onto it leaves the bars in place and lets only their heights follow the results.
 */
export const rebinOntoGrid = (
  live: PriceHistogram,
  grid: PriceHistogram,
  ceiling?: number | null,
): Pick<PriceHistogram, "buckets" | "catch_all"> => {
  const top = Math.max(
    ceiling ?? 0,
    grid.max + grid.interval,
    live.max + live.interval,
    live.catch_all ? live.catch_all.lo + live.interval : 0,
  );
  const slices = toSlices(live, top);
  const share = (lo: number, hi: number) =>
    slices.reduce((total, slice) => {
      const from = Math.max(lo, slice.lo);
      const to = Math.min(hi, slice.hi);
      const width = slice.hi - slice.lo;
      return to > from && width > 0 ? total + (slice.count * (to - from)) / width : total;
    }, 0);

  return {
    buckets: grid.buckets.map((bucket) => ({
      ...bucket,
      count: Math.round(share(bucket.lo, bucket.hi)),
    })),
    catch_all: grid.catch_all
      ? { ...grid.catch_all, count: Math.round(share(grid.catch_all.lo, top)) }
      : undefined,
  };
};
